import {
  clearMacAIChatTokenCache,
  getMacAIChatTokens,
  MAC_AI_API_BASE,
  type MacAIChatTokens
} from './fusionNowAuth';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MacAIConversation {
  id: string;
  title: string;
  created_at?: string;
  updated_at?: string;
  user_id?: string;
}

export interface MacAIHistoryMessage {
  question: string;
  response: string;
  created_at: string;
}

/**
 * Widget returned by the AI when it wants to render an interactive iframe.
 * This type is the source of truth — chat.types.ts (Task 4) imports it
 * to add `widget?` to MessageMetadata.
 */
export interface MacAIWidget {
  /** e.g. "usecase_explorer" */
  type: string;
  /** Only 'srcdoc' is supported by MacAIChatWidget.tsx */
  render_type: 'srcdoc' | string;
  /** Full HTML document rendered as iframe srcdoc */
  html: string;
}

/**
 * Emitted by sendMacAIMessage on every SSE chunk.
 * Exactly one of content / statusText / widget will be set per chunk.
 */
export interface MacAIStreamChunk {
  /** Accumulated text so far (text branch) */
  content?: string;
  /** Transient status text — "Analyzing…" etc. (status branch) */
  statusText?: string;
  /** Interactive widget payload (widget branch) */
  widget?: MacAIWidget;
  /** false = this is the terminal chunk for this branch */
  isStreaming: boolean;
}

// ── Authenticated fetch ───────────────────────────────────────────────────────

/**
 * fetch() against the MAC AI API with both auth headers (same as
 * macAIService.js in 20miles-react-app). A 401 means MAC AI refused a token,
 * so the cached tokens are dropped and the call is retried once with fresh ones.
 */
async function macAIFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = ({ accessToken, userToken }: MacAIChatTokens) =>
    fetch(`${MAC_AI_API_BASE}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Token': userToken,
        ...init.headers,
      },
    });

  const res = await send(await getMacAIChatTokens());
  if (res.status !== 401) return res;

  clearMacAIChatTokenCache();
  return send(await getMacAIChatTokens());
}

// ── SSE streaming ─────────────────────────────────────────────────────────────

/**
 * Sends a message to the MAC AI Chat API and streams the response via SSE.
 *
 * SSE 3-way branch (confirmed from macAIService.js in 20miles-react-app):
 *   { render: true, widget: {...} }          → widget (iframe)
 *   { is_status: true, response: "..." }     → transient status text
 *   { response: "...", responseStreaming }   → text accumulation
 *
 * @param signal - AbortController signal. Abort to cancel mid-stream
 *                 (e.g. when the panel unmounts). AbortError is swallowed —
 *                 returns whatever was accumulated so far.
 */
export async function sendMacAIMessage(
  prompt: string,
  conversationId: string,
  uid: string,
  onChunk: (chunk: MacAIStreamChunk) => void,
  signal?: AbortSignal
): Promise<{ response: string; errorMessage?: string }> {
  const res = await macAIFetch('/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Conversation-ID': conversationId,
    },
    body: JSON.stringify({ human_prompt: prompt, uid }),
    signal,
  });

  if (!res.ok) throw new Error(`MAC AI chat request failed: HTTP ${res.status}`);
  if (!res.body) throw new Error('MAC AI chat: response has no body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullResponse = '';
  let lastError = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE lines are \n or \r\n delimited. Keep the last partial line in the buffer.
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;

        try {
          const chunk = JSON.parse(trimmed.slice(6));

          // Capture non-empty error messages (empty string in normal chunks — falsy, ignored)
          if (chunk.error_message) lastError = chunk.error_message;

          if (chunk.render && chunk.widget &&
              typeof chunk.widget.html === 'string' &&
              typeof chunk.widget.render_type === 'string') {
            // ── Widget branch ──────────────────────────────────────────────
            // AI returned an interactive widget — render as iframe, not text.
            // Shape is validated above before cast; MacAIChatWidget still
            // guards html.length >= 100 and render_type === 'srcdoc'.
            onChunk({ widget: chunk.widget as MacAIWidget, isStreaming: false });
            // Stop processing — trailing server lines after the widget would
            // re-trigger the text branch and set isStreaming back to true.
            return { response: fullResponse };

          } else if (chunk.is_status && chunk.response) {
            // ── Status branch ─────────────────────────────────────────────
            // Transient status message — shown as italic text, never accumulated
            // into fullResponse. Panel renders this inline, not via ChatMessage.
            onChunk({ statusText: chunk.response as string, isStreaming: true });

          } else if (chunk.response) {
            // ── Text branch ───────────────────────────────────────────────
            // Normal case: server streams deltas, accumulate and emit running total.
            //
            // Duplicate-response pattern: the MAC AI server streams partial tokens,
            // emits \n\n, then re-streams the COMPLETE response from the beginning.
            // Result without correction: "partial text\n\ncomplete response"
            // (ReactMarkdown renders this as two paragraphs with the same text).
            //
            // Detection: after appending each delta, check if fullResponse contains
            // \n\n where the text AFTER the separator starts with (≥15 chars of)
            // the text BEFORE the separator. If so, the "after" part is the complete
            // response — discard the partial "before" and track only "after".
            fullResponse += chunk.response as string;

            const sepIdx = fullResponse.indexOf('\n\n');
            if (sepIdx >= 15) {
              const before = fullResponse.substring(0, sepIdx);
              const after  = fullResponse.substring(sepIdx + 2);
              const checkLen = Math.min(after.length, before.length, 30);
              if (checkLen >= 15 && after.startsWith(before.substring(0, checkLen))) {
                fullResponse = after;
              }
            }

            onChunk({
              content: fullResponse,
              isStreaming: chunk.responseStreaming !== false,
            });
          }
        } catch {
          // Skip malformed / non-JSON SSE lines silently
        }
      }
    }
  } catch (err) {
    // AbortError = panel unmounted or user cancelled — not a real error.
    // Return whatever text was accumulated so far.
    if ((err as Error).name === 'AbortError') {
      return { response: fullResponse };
    }
    throw err;
  } finally {
    // Always release the reader — prevents locked ReadableStream if we
    // exit early (AbortError, throw, or widget branch finishing before done).
    reader.cancel().catch(() => {/* ignore cancel errors */});
  }

  return lastError
    ? { response: fullResponse, errorMessage: lastError }
    : { response: fullResponse };
}

// ── Conversation CRUD ─────────────────────────────────────────────────────────

/** Creates a new conversation. Response is the conversation object directly (no wrapper). */
export async function createMacAIConversation(uid: string): Promise<MacAIConversation> {
  const res = await macAIFetch('/conversations', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ uid }),
  });
  if (!res.ok) throw new Error(`createConversation failed: HTTP ${res.status}`);
  // Response shape: { id, title, user_id } — flat, no data wrapper
  return await res.json() as MacAIConversation;
}

/** Returns all conversations for the given uid, newest first. */
export async function getMacAIConversations(uid: string): Promise<MacAIConversation[]> {
  const res = await macAIFetch(`/conversations?uid=${encodeURIComponent(uid)}`);
  if (!res.ok) throw new Error(`getConversations failed: HTTP ${res.status}`);
  const data = await res.json();
  // Raw API response shape: { conversations: [...] }
  return (data?.conversations ?? []) as MacAIConversation[];
}

/** Returns all messages for a given conversation ID. */
export async function getMacAIConversationMessages(
  conversationId: string
): Promise<MacAIHistoryMessage[]> {
  const res = await macAIFetch(
    `/conversations/${encodeURIComponent(conversationId)}/messages`
  );
  if (!res.ok) throw new Error(`getConversationMessages failed: HTTP ${res.status}`);
  const data = await res.json();
  // Raw API response shape: { messages: [...] }
  return (data?.messages ?? []) as MacAIHistoryMessage[];
}
